/**
 * Onaylar on the phone: the queue (rows only, never an approve button) and the full-screen
 * detail where the decision happens. Swiping moves between items and never approves.
 */

import { type TouchEvent, useRef, useState } from 'react';
import { REJECT_REASON_LABELS } from '../api/types.js';
import { usePanel, useStore } from '../app/context.js';
import { Link, navigate } from '../app/router.js';
import { counts, orderedProposals, outboxWaiting, requiresHold } from '../app/selectors.js';
import { DecisionBar } from '../components/evidence.js';
import type { HoldHandle } from '../components/HoldButton.js';
import { Icon } from '../components/icons.js';
import { AppBar, BottomTabBar, DetailAppBar, StatusArea } from '../components/shell.js';
import { DoneRow } from '../components/widgets.js';
import { formatAge, formatClock, formatLongDate, formatRelativeDay } from '../lib/format.js';
import { ApprovalsSection } from './Bugun.js';
import { Accent, EmptyState, Loading, Segmented, SummaryHeadline, TierSubline } from './common.js';
import { useDecision } from './decision.js';
import { ProposalBody } from './ProposalBody.js';

type QueueTab = 'bekleyen' | 'yapilanlar' | 'reddedilen';

export function Onaylar() {
  const state = usePanel();
  const store = useStore();
  const [tab, setTab] = useState<QueueTab>('bekleyen');
  const snapshot = state.snapshot;
  const now = state.now;
  if (!snapshot) {
    return (
      <>
        <AppBar title="Onaylar" subtitle={formatLongDate(now)} />
        <Loading />
        <BottomTabBar active="onaylar" />
      </>
    );
  }
  const c = counts(snapshot);
  const lastDone = snapshot.done[0];
  return (
    <>
      <AppBar title="Onaylar" subtitle={formatLongDate(now)} />
      <StatusArea />
      <SummaryHeadline
        title={
          c.approvals === 0 ? (
            'Kuyruk boş'
          ) : (
            <>
              <Accent>{c.approvals}</Accent> onay bekliyor
            </>
          )
        }
        subline={
          c.approvals === 0 ? (
            lastDone ? (
              `son onay ${formatClock(lastDone.at)}`
            ) : (
              'Yeni öneri gelince Telegram haber verir.'
            )
          ) : (
            <TierSubline snapshot={snapshot} now={now} />
          )
        }
      />
      <Segmented
        label="Onay durumu"
        controls="onay-panel"
        value={tab}
        onChange={setTab}
        options={[
          { id: 'bekleyen', label: 'Bekleyen', count: c.approvals },
          { id: 'yapilanlar', label: 'Yapılanlar', count: null },
          { id: 'reddedilen', label: 'Reddedilen', count: null },
        ]}
      />
      <main
        className="phone-main phone-main--queue"
        id="onay-panel"
        role="tabpanel"
        aria-labelledby={`tab-onay-panel-${tab}`}
      >
        {tab === 'bekleyen' ? (
          c.approvals === 0 ? (
            <EmptyState
              title={lastDone ? `Kuyruk boş · son onay ${formatClock(lastDone.at)}` : 'Kuyruk boş'}
              text="Ekip çalışıyor. Bir şey gelince Telegram'dan haber veririz."
            />
          ) : (
            <div className="queue-list">
              <ApprovalsSection snapshot={snapshot} now={now} headingId="onay-panel" grouped />
            </div>
          )
        ) : null}
        {tab === 'yapilanlar' ? (
          <div className="done-list">
            {outboxWaiting(snapshot) > 0 ? (
              <Link to="/gonderilecekler" className="done-row done-row--link">
                <span className="done-row__text">
                  <span className="done-row__title">Gönderilecekler · {outboxWaiting(snapshot)} bekliyor</span>
                  <span className="done-row__meta">E-posta cevapları, teslimat mesajları ve yeni Etsy ilanları</span>
                </span>
                <Icon name="chevron-right" size={16} className="ink-3" />
              </Link>
            ) : null}
            {state.awaitingSend.map((item) => (
              <div key={item.proposalId} className="done-row">
                <span className="done-row__text">
                  <span className="done-row__title">{item.title}</span>
                  <span className="done-row__meta">
                    {item.copy ? "Gönderilmeyi bekliyor · Etsy'de sen gönderirsin" : 'Elle yapılmayı bekliyor'}
                  </span>
                </span>
                <button type="button" className="text-btn" onClick={() => void store.markSent(item.proposalId)}>
                  {item.copy ? 'Gönderdim' : 'Yaptım'}
                </button>
              </div>
            ))}
            {snapshot.done.map((item) => (
              <DoneRow key={item.id} item={item} now={now} onRevert={() => void store.revert(item.id)} />
            ))}
          </div>
        ) : null}
        {tab === 'reddedilen' ? (
          state.rejected.length === 0 ? (
            <EmptyState
              title="Son 7 günde reddettiğin öneri yok"
              text="Reddettiğinde gerekçe ajana gider ve burada görünür."
            />
          ) : (
            <div className="done-list">
              {state.rejected.map((item) => (
                <div key={item.proposalId} className="done-row">
                  <span className="done-row__text">
                    <span className="done-row__title">{item.title}</span>
                    <span className="done-row__meta">
                      {formatRelativeDay(item.at, now)} · <span className="danger">Reddettin</span> ·{' '}
                      {REJECT_REASON_LABELS[item.reasonCode]}
                    </span>
                  </span>
                </div>
              ))}
            </div>
          )
        ) : null}
      </main>
      <BottomTabBar active="onaylar" />
    </>
  );
}

function AllDone() {
  return (
    <div className="all-done">
      <Icon name="check" size={24} stroke={2} className="ok" />
      <p className="all-done__title">Hepsi bitti · her şey yolunda</p>
      <Link to="/" className="btn btn--primary">
        Bugün'e dön
      </Link>
    </div>
  );
}

/** Full-screen approval detail on the phone (artboard F_Onay). */
export function OnayDetay({ id }: { id: string }) {
  const state = usePanel();
  const snapshot = state.snapshot;
  const holdRef = useRef<HoldHandle>(null);
  const touch = useRef<{ x: number; y: number } | null>(null);
  const proposals = snapshot ? orderedProposals(snapshot) : [];
  const index = proposals.findIndex((proposal) => proposal.id === id);
  const proposal = proposals[index] ?? null;
  const decision = useDecision(proposal, (nextId) => {
    if (nextId) navigate(`/onay/${nextId}`, { replace: true });
  });

  if (!snapshot) {
    return <Loading />;
  }

  const back = { label: 'Kuyruk', to: '/onaylar' };
  const pending = state.pending.find((item) => item.proposal.id === id);

  if (!proposal) {
    const approvedT4 = pending && requiresHold(pending.proposal);
    return (
      <>
        <DetailAppBar
          back={back}
          title="Onay"
          titleMono={null}
          segments={null}
          metaKey="Kuyruk"
          metaValue={`${proposals.length}`}
        />
        <main className="detail-main detail-main--plain">
          {approvedT4 ? (
            <div className="all-done">
              <Icon name="check" size={24} stroke={2} className="ok" />
              <p className="all-done__title">Onaylandı · {pending.proposal.title}</p>
              <p className="all-done__text">Yürütücü 10 sn bekler; bu sürede alttaki bildirimden geri alabilirsin.</p>
              <Link to="/onaylar" className="btn btn--primary">
                Kuyruğa dön
              </Link>
            </div>
          ) : proposals.length === 0 ? (
            <AllDone />
          ) : (
            <div className="all-done">
              <p className="all-done__title">Bu onay artık kuyrukta değil</p>
              <Link to={`/onay/${proposals[0]?.id ?? ''}`} className="btn btn--primary">
                Sıradakini aç
              </Link>
            </div>
          )}
        </main>
      </>
    );
  }

  const onTouchStart = (event: TouchEvent) => {
    const point = event.touches[0];
    touch.current = point ? { x: point.clientX, y: point.clientY } : null;
  };
  const onTouchEnd = (event: TouchEvent) => {
    const start = touch.current;
    const point = event.changedTouches[0];
    touch.current = null;
    if (!start || !point) return;
    const dx = point.clientX - start.x;
    const dy = point.clientY - start.y;
    if (Math.abs(dx) < 70 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
    const target = proposals[index + (dx < 0 ? 1 : -1)];
    if (target) navigate(`/onay/${target.id}`, { replace: true });
  };

  const titleId = `onay-title-${proposal.id}`;
  return (
    <>
      <DetailAppBar
        back={back}
        title="Onay"
        titleMono={`${index + 1}/${proposals.length}`}
        segments={{ total: proposals.length, current: index + 1 }}
        metaKey="Bekliyor"
        metaValue={formatAge(state.now - proposal.queuedAt)}
      />
      <main className="detail-main" aria-labelledby={titleId} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
        <ProposalBody proposal={proposal} snapshot={snapshot} titleId={titleId} />
      </main>
      <DecisionBar
        proposal={proposal}
        blocker={decision.blocker}
        holdRef={holdRef}
        onApprove={(via) => void decision.approve(via)}
        onEdit={decision.edit}
        onReject={decision.reject}
      />
    </>
  );
}
